// Work in progress kept on this device (docs/plans/dialog-safety.md): Receive stock and a new
// purchase order save what was typed as it is typed, so closing the dialog, a reload for an
// update or a flat battery loses nothing. A draft is never synced: it lives in this browser
// only, per business, for a week, and signing out deletes it (it holds costs). It keeps ids
// and typed values, never whole rows: the screen reads the products again when it is opened.

export const DRAFT_VERSION = 1
/** A draft older than this is thrown away when it is next read. */
export const DRAFT_DAYS = 7

const PREFIX = `ims.draft.v${DRAFT_VERSION}.`

export type DraftKind = 'receive' | 'po' | 'product'

export interface Draft<T> {
  savedAt: string
  value: T
}

/** The part of `Storage` a draft needs; tests pass a Map-backed one. */
export type DraftStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>

const storeOf = (store?: DraftStore): DraftStore | null => {
  try {
    return store ?? globalThis.localStorage ?? null
  } catch {
    return null
  }
}

/** `id` is `free` (receiving without an order), a purchase order's id, or `new`. */
export function draftKey(tenantId: string, kind: DraftKind, id: string): string {
  return `${PREFIX}${tenantId}.${kind}.${id}`
}

/**
 * The draft under `key`, or null. A draft that cannot be read, fails `parse`, or is older than
 * `DRAFT_DAYS` is deleted.
 */
export function readDraft<T>(
  key: string,
  parse: (value: unknown) => T | null,
  now: Date = new Date(),
  store?: DraftStore,
): Draft<T> | null {
  const s = storeOf(store)
  if (!s) return null
  try {
    const text = s.getItem(key)
    if (text === null) return null
    const raw: unknown = JSON.parse(text)
    if (raw && typeof raw === 'object' && 'savedAt' in raw && 'value' in raw) {
      const savedAt = String(raw.savedAt)
      const age = now.getTime() - Date.parse(savedAt)
      const value = age >= 0 && age < DRAFT_DAYS * 86_400_000 ? parse(raw.value) : null
      if (value !== null) return { savedAt, value }
    }
    s.removeItem(key)
  } catch {
    try {
      s.removeItem(key)
    } catch {
      // Storage refused: nothing more to do.
    }
  }
  return null
}

export function saveDraft<T>(key: string, value: T, now: Date = new Date(), store?: DraftStore) {
  try {
    storeOf(store)?.setItem(key, JSON.stringify({ savedAt: now.toISOString(), value }))
  } catch {
    // Private mode or a full disk: the work still goes on, only without a draft.
  }
}

export function clearDraft(key: string, store?: DraftStore) {
  try {
    storeOf(store)?.removeItem(key)
  } catch {
    // As above.
  }
}

/** Every draft of one business on this device (sign-out). */
export function clearTenantDrafts(tenantId: string, store?: DraftStore) {
  const s = storeOf(store)
  if (!s) return
  try {
    const mine: string[] = []
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i)
      if (k?.startsWith(`${PREFIX}${tenantId}.`)) mine.push(k)
    }
    for (const k of mine) s.removeItem(k)
  } catch {
    // As above.
  }
}
